import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, XCircle } from "lucide-react";
import { getPaymentStatus } from "../lib/api";
import { trackPurchase } from "../lib/tracking";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";

const MAX_POLLS = 8;
const INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [orderId, setOrderId] = useState("");
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let timer;

    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        setOrderId(res.order_id || "");
        if (res.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            trackPurchase(res.order_id, res.amount || 0);
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (["failed", "expired"].includes(res.payment_status)) {
          setState("error");
          return;
        }
      } catch (err) {
        // Network hiccup — log and keep polling until max attempts
        console.error("Erro ao verificar estado do pagamento:", err);
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, INTERVAL);
    };

    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clearCart]);

  if (state === "checking") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-6" />
        <h1 className="font-serif text-2xl mb-2">A confirmar o pagamento...</h1>
        <p className="text-muted-foreground">Um momento, estamos a validar a tua compra.</p>
      </div>
    );
  }

  if (state === "error") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-6">
          <XCircle className="h-8 w-8 text-destructive" />
        </div>
        <h1 className="font-serif text-3xl mb-3">Algo correu mal</h1>
        <p className="text-muted-foreground mb-8">Não foi possível confirmar o pagamento. Se o valor foi cobrado, contacta o apoio.</p>
        <Link to="/checkout"><Button className="rounded-full h-12 px-8">Tentar novamente</Button></Link>
      </div>
    );
  }

  if (state === "pending") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-6" />
        <h1 className="font-serif text-2xl mb-2">Pagamento em processamento</h1>
        <p className="text-muted-foreground mb-8">O teu pagamento está a ser processado. Enviaremos a confirmação por email.</p>
        <Link to="/loja"><Button variant="outline" className="rounded-full h-12 px-8">Continuar a comprar</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
        <Check className="h-8 w-8 text-primary" />
      </div>
      <h1 className="font-serif text-3xl md:text-4xl mb-3">Obrigado pela tua compra!</h1>
      <p className="text-muted-foreground mb-2">
        O teu pagamento foi confirmado com sucesso. Enviámos os detalhes para o teu email.
      </p>
      {orderId && (
        <p className="text-sm text-muted-foreground mb-8">
          Nº da encomenda: <span className="font-mono text-foreground">{orderId.slice(0, 8).toUpperCase()}</span>
        </p>
      )}
      <Link to="/loja">
        <Button className="rounded-full h-12 px-8">Continuar a comprar</Button>
      </Link>
    </div>
  );
}
